import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';
import {
  evaluateCondition,
  formatBundleItems,
  numberCheck,
} from '../../inspection';
import { inputTypes } from '../../../constants/strings';
import { Answer } from './assessment_answers';
import { customColors } from '../../misc';
import { actionsKeys } from '../../../components/question_menu/conditionalActions/conditionalAction.utils';
import { textStyles } from '../styles';
import { AssessmentComments } from './assessment_comments';
import { riskLevels } from '../../audits';
import { LexicalPdf } from '../rich_text_pdf';

const fontSize = 8;
const blue = customColors.BLUE;
const textPrimary = '#1d1d1f';
const textSecondary = customColors.SECONDARY;

const styles = StyleSheet.create({
  bundleIcon: {
    height: '12px',
    width: '12px',
  },
  categoryContainer: {
    flexDirection: 'column',
    gap: 10,
  },
  categoryHeader: {
    fontSize: fontSize + 5,
    fontFamily: 'eina03bold',
    color: blue,
    marginBottom: 3,
    marginTop: 12,
  },
  category: {
    flexDirection: 'column',
  },
  bundleAnswer: {
    flexDirection: 'column',
    gap: 5,
  },
  text: {
    fontSize,
    color: textPrimary,
    fontFamily: 'eina03regular',
    flexWrap: 'wrap',
    wordBreak: 'break-word',
  },
  limitsText: {
    fontSize: fontSize - 1,
    color: textSecondary,
    fontFamily: 'eina03regular',
  },
  semiBoldText: {
    fontSize: fontSize + 2,
    color: textPrimary,
    fontFamily: 'eina03semibold',
  },
  bundle: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 5,
    marginTop: 3,
    marginBottom: 3,
  },
  answer: {
    flexDirection: 'column',
    gap: 7,
    marginTop: 3,
    marginBottom: 3,
  },
  conditionalAction: {
    marginTop: 2,
    marginLeft: 10,
    paddingLeft: 6,
    borderLeft: '2px solid #e0e0e0',
  },
  conditionalAnswer: {
    marginLeft: 3,
  },
  semiBoldTextSecondary: {
    fontSize: fontSize + 2,
    color: textSecondary,
    fontFamily: 'eina03semibold',
  },
});

export const Category = ({
  category,
  categoryId,
  isBundle,
  questions,
  answers,
  isAudit,
  correctiveActions = [],
  showComments,
  props,
}) => {
  const { bundleMap } = formatBundleItems(answers);
  const getAnswer = (answers, question) => {
    return answers?.find((a) => a.questionId === question.id)
      ? answers?.find((a) => a.questionId === question.id)?.value
      : null;
  };
  return (
    <View style={styles.category}>
      <View style={styles.categoryContainer}>
        <Text style={styles.categoryHeader}>{category}</Text>
      </View>
      {isAudit ? (
        <View>
          {questions?.map((question) => {
            const value = getAnswer(answers, question);
            const itemCorrectiveAction =
              correctiveActions?.find((c) => c.question?.id === question.id) ||
              [];
            const answer = answers?.find((a) => a.questionId === question.id);
            return (
              <View style={textStyles.row} key={question.id}>
                <View style={textStyles.fontSizeMd}>
                  <Text style={textStyles.leftText}>{question.title}</Text>
                  {!!itemCorrectiveAction?.riskLevel ? (
                    <Text
                      style={
                        textStyles[
                          `${
                            riskLevels[itemCorrectiveAction.riskLevel]?.color
                          }Left`
                        ]
                      }
                    >
                      {`${
                        itemCorrectiveAction.riskLevel === 'OBSERVATION'
                          ? 'OFI'
                          : itemCorrectiveAction.riskLevel
                      } ${
                        itemCorrectiveAction.riskLevel === 'OBSERVATION'
                          ? ''
                          : 'RISK'
                      }`}
                    </Text>
                  ) : null}
                  <Text style={styles.text}>
                    {itemCorrectiveAction.narrative}
                  </Text>
                  {!!showComments && (
                    <View style={textStyles.commentContainer}>
                      <AssessmentComments answer={answer} />
                    </View>
                  )}
                </View>
                <View>
                  <Text
                    style={
                      value === 'PASS'
                        ? textStyles.green
                        : value === 'FAIL'
                        ? textStyles.red
                        : textStyles.rightText
                    }
                  >
                    {value === 'NOT_APPLICABLE' ? 'N/A' : value || '-'}
                  </Text>
                  <Text style={textStyles.rightText}>
                    {itemCorrectiveAction.externalId}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>
      ) : isBundle ? (
        <>
          {!!bundleMap[categoryId] &&
            Object.entries(bundleMap[categoryId]).map(
              ([bundleId, bundleAnswers], idx) => (
                <View key={`bundle-${bundleId}`} style={styles.bundle}>
                  <Text style={styles.semiBoldText}>{`${idx + 1}.`}</Text>
                  <View style={styles.bundleAnswer}>
                    {Object.values(bundleAnswers)?.map((answer, index) => {
                      if (answer.categoryId !== categoryId) return [];
                      const question = questions?.find(
                        (question) => question.id === answer.questionId,
                      );
                      if (!question) return null;
                      return (
                        <View
                          key={`question-${question.id}-${bundleId}-${index}`}
                        >
                          {question?.inputType === 'MESSAGE' ? (
                            <View key={index}>
                              {LexicalPdf({ content: question.title })}
                            </View>
                          ) : (
                            <View>
                              <Text style={styles.semiBoldText}>
                                {question.title}
                              </Text>
                              {question.inputType === inputTypes.number &&
                              !!question.additionalData ? (
                                <Text style={styles.limitsText}>
                                  {numberCheck(question.additionalData)}
                                </Text>
                              ) : null}
                              <Answer
                                answer={answer}
                                inputType={question.inputType}
                                additional={question.additionalData}
                                question={question}
                              >
                                {answer?.value}
                              </Answer>
                            </View>
                          )}
                        </View>
                      );
                    })}
                  </View>
                </View>
              ),
            )}
        </>
      ) : (
        <View style={styles.answer}>
          {questions?.map((question, index) => {
            const answer = answers?.find((a) => a.questionId === question.id);
            const { conditionalAction } = question || {};
            const matchingActions = Array.isArray(conditionalAction)
              ? conditionalAction
                  .filter((ca) =>
                    evaluateCondition(answer?.value, {
                      inputType: question?.inputType,
                      conditionalAction: ca,
                    }),
                  )
                  .flatMap((ca) => ca.actions ?? [])
                  .sort(
                    (a, b) =>
                      (a.sortIndex ?? Infinity) - (b.sortIndex ?? Infinity),
                  )
              : [];
            return (
              <View key={`question-${question.id}-${index}`}>
                {question.inputType === 'MESSAGE' ? (
                  <View key={index}>
                    {LexicalPdf({ content: question.title })}
                  </View>
                ) : (
                  <View>
                    <Text style={styles.semiBoldText}>{question.title}</Text>
                    {question.inputType === inputTypes.number &&
                    !!question.additionalData ? (
                      <Text style={styles.limitsText}>
                        {numberCheck(question.additionalData)}
                      </Text>
                    ) : null}

                    <Answer
                      answer={answer}
                      inputType={question.inputType}
                      additional={question.additionalData}
                      question={question}
                    >
                      {getAnswer(answers, question)}
                    </Answer>
                  </View>
                )}
                {(() => {
                  if (!matchingActions.length) return null;
                  const groups = {
                    questions: [],
                    assessments: [],
                    files: [],
                    notifications: [],
                    messages: [],
                  };
                  for (const {
                    id,
                    action,
                    actionable,
                    additionalData,
                  } of matchingActions) {
                    switch (action) {
                      case actionsKeys.followUpQuestion:
                        if (actionable)
                          groups.questions.push({ id, actionable });
                        break;
                      case actionsKeys.followUpTemplate:
                        if (actionable)
                          groups.assessments.push({ id, actionable });
                        break;
                      case actionsKeys.showFile:
                        if (actionable) groups.files.push({ id, actionable });
                        break;
                      case actionsKeys.popUp:
                      case actionsKeys.showMessage:
                        groups.messages.push({ id, action, additionalData });
                        break;
                      case actionsKeys.sendNotification:
                        if (actionable)
                          groups.notifications.push({ id, actionable });
                        break;
                      default:
                        break;
                    }
                  }
                  return (
                    <View style={styles.conditionalAction}>
                      {groups.questions.length > 0 && (
                        <View>
                          <Text style={styles.semiBoldTextSecondary}>
                            Follow Up Questions
                          </Text>
                          {groups.questions.map(({ id, actionable }) => {
                            const conditionalAns = answers?.find(
                              (a) => a.questionId === actionable?.id,
                            );
                            return (
                              <View
                                style={styles.conditionalAnswer}
                                key={`action-question-${id}`}
                              >
                                {actionable?.inputType === 'MESSAGE' ? (
                                  <View>
                                    {LexicalPdf({ content: actionable?.title })}
                                  </View>
                                ) : (
                                  <View>
                                    <Text style={styles.semiBoldText}>
                                      {actionable?.title}
                                    </Text>
                                    {actionable?.inputType ===
                                      inputTypes.number &&
                                    !!actionable?.additionalData ? (
                                      <Text style={styles.limitsText}>
                                        {numberCheck(
                                          actionable?.additionalData,
                                        )}
                                      </Text>
                                    ) : null}
                                    <Answer
                                      answer={conditionalAns}
                                      inputType={actionable?.inputType}
                                      additional={actionable?.additionalData}
                                      question={actionable}
                                    >
                                      {getAnswer(answers, actionable)}
                                    </Answer>
                                  </View>
                                )}
                              </View>
                            );
                          })}
                        </View>
                      )}
                      {groups.assessments.length > 0 && (
                        <View>
                          <Text style={styles.semiBoldTextSecondary}>
                            Follow Up Assessments
                          </Text>
                          {groups.assessments.map(({ id, actionable }) => (
                            <View
                              style={styles.conditionalAnswer}
                              key={`action-assessment-${id}`}
                            >
                              <Text style={styles.text}>
                                {actionable?.title}
                              </Text>
                            </View>
                          ))}
                        </View>
                      )}
                      {groups.files.length > 0 && (
                        <View>
                          <Text style={styles.semiBoldTextSecondary}>
                            Files
                          </Text>
                          {groups.files.map(({ id, actionable }) => (
                            <View
                              style={styles.conditionalAnswer}
                              key={`action-file-${id}`}
                            >
                              <Text style={styles.text}>
                                {actionable?.name}
                              </Text>
                            </View>
                          ))}
                        </View>
                      )}
                      {groups.notifications.length > 0 && (
                        <View>
                          <Text style={styles.semiBoldTextSecondary}>
                            Notified
                          </Text>
                          {groups.notifications.map(({ id, actionable }) => (
                            <View
                              style={styles.conditionalAnswer}
                              key={`action-notification-${id}`}
                            >
                              <Text
                                style={styles.text}
                              >{`${actionable?.firstName} ${actionable?.lastName}`}</Text>
                            </View>
                          ))}
                        </View>
                      )}
                      {groups.messages.length > 0 && (
                        <View>
                          <Text style={styles.semiBoldTextSecondary}>
                            Messages
                          </Text>
                          {groups.messages.map(
                            ({ id, action, additionalData }) => (
                              <View
                                style={styles.conditionalAnswer}
                                key={`action-message-${id}`}
                              >
                                {action === actionsKeys.showMessage ? (
                                  <LexicalPdf
                                    content={additionalData?.message}
                                  />
                                ) : (
                                  <Text style={styles.semiBoldText}>
                                    {additionalData?.message}
                                  </Text>
                                )}
                              </View>
                            ),
                          )}
                        </View>
                      )}
                    </View>
                  );
                })()}
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
};
